import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { getCltvSectionDefinition } from "./CltvDefinitions";
import {
  CltvSectionTitle,
  EvolutionTooltip,
  FloatingTooltip,
  type EvolutionSeriesKey,
} from "./CltvDashboardShared";
import "../Styles/CltvSegmentEvolution.scss";

type EvolutionPoint = {
  label: string;
} & Partial<Record<EvolutionSeriesKey, number>>;

type ActiveEvolutionSeries = Array<{
  key: EvolutionSeriesKey;
  labelKey: string;
  color: string;
  latestValue: number;
  maxValue: number;
}>;

type CltvSegmentEvolutionProps = {
  loading: boolean;
  chartsError: string | null;
  segmentEvolution: EvolutionPoint[];
  activeEvolutionSeries: ActiveEvolutionSeries;
  formatMonthYear: (_value: string) => string;
  formatCountAxis: (_value: number) => string;
};

const CltvSegmentEvolution: FC<CltvSegmentEvolutionProps> = ({
  loading,
  chartsError,
  segmentEvolution,
  activeEvolutionSeries,
  formatMonthYear,
  formatCountAxis,
}) => {
  const { t } = useTranslation();
  const [evolutionHover, setEvolutionHover] = useState<{ x: number; y: number; index: number } | null>(
    null,
  );
  const handleEvolutionChartMouseMove = useCallback((state: unknown, event: unknown) => {
    const chartState = state as {
      isTooltipActive?: boolean;
      activeTooltipIndex?: number;
    } | null;
    const mouseEvent = event as { clientX?: number; clientY?: number } | null;
    if (
      chartState?.isTooltipActive &&
      typeof chartState.activeTooltipIndex === "number" &&
      typeof mouseEvent?.clientX === "number" &&
      typeof mouseEvent?.clientY === "number"
    ) {
      setEvolutionHover({
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
        index: chartState.activeTooltipIndex,
      });
    }
  }, []);
  const handleEvolutionChartMouseLeave = useCallback(() => setEvolutionHover(null), []);
  const evolutionTooltipPayload = useMemo(() => {
    if (!evolutionHover) {
      return null;
    }
    const point = segmentEvolution[evolutionHover.index];
    if (!point) {
      return null;
    }
    return {
      label: point.label,
      payload: activeEvolutionSeries.map(series => ({
        name: series.key,
        value: Number(point[series.key] ?? 0),
        color: series.color,
      })),
    };
  }, [evolutionHover, segmentEvolution, activeEvolutionSeries]);

  return (
    <article className="cltv-dashboard__panel cltv-dashboard__panel--evolution" data-pdf-block="evolution-chart">
      <div className="cltv-dashboard__evolution-header">
        <CltvSectionTitle
          title={t("cltv.sections.segmentEvolution")}
          tooltip={getCltvSectionDefinition("segmentEvolution", t)}
        />
        {activeEvolutionSeries.length > 0 && (
          <div className="cltv-dashboard__line-legend">
            {activeEvolutionSeries.map(series => (
              <span key={series.key}>
                <i style={{ backgroundColor: series.color }} />
                {t(series.labelKey)}
              </span>
            ))}
          </div>
        )}
        <p className="cltv-dashboard__panel-note">
          {t("cltv.notes.segmentEvolution")}
        </p>
      </div>
      {loading ? (
        <LoadingSpinner compact />
      ) : chartsError && segmentEvolution.length === 0 ? (
        <p className="cltv-dashboard__empty" role="alert">
          {chartsError}
        </p>
      ) : segmentEvolution.length === 0 ? (
        <p className="cltv-dashboard__empty">{t("cltv.empty.noEvolution")}</p>
      ) : (
        <div className="cltv-dashboard__chart">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={segmentEvolution}
              margin={{ top: 16, right: 20, left: 10, bottom: 10 }}
              onMouseMove={handleEvolutionChartMouseMove}
              onMouseLeave={handleEvolutionChartMouseLeave}
            >
              <CartesianGrid stroke="#e8eef8" strokeDasharray="3 4" vertical={false} />
              <XAxis
                dataKey="label"
                tickFormatter={formatMonthYear}
                tick={{ fontSize: 12, fill: "#8aa0bf" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={formatCountAxis}
                width={52}
                tick={{ fontSize: 12, fill: "#8aa0bf" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip cursor={{ stroke: "#c6d3e6", strokeWidth: 1 }} content={() => null} />
              {activeEvolutionSeries.map(series => (
                <Line
                  key={series.key}
                  type="linear"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={2}
                  strokeOpacity={0.9}
                  dot={{ r: 4, fill: "#ffffff", stroke: series.color, strokeWidth: 2 }}
                  activeDot={{ r: 5.5, fill: "#ffffff", stroke: series.color, strokeWidth: 2 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          {evolutionTooltipPayload ? (
            <FloatingTooltip
              x={evolutionHover!.x}
              y={evolutionHover!.y}
              contentKey={evolutionHover!.index}
            >
              <EvolutionTooltip
                active
                label={evolutionTooltipPayload.label}
                payload={evolutionTooltipPayload.payload}
                formatDate={formatMonthYear}
              />
            </FloatingTooltip>
          ) : null}
        </div>
      )}
    </article>
  );
};

export default CltvSegmentEvolution;
